import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import Confetti from 'growth-onboarding-confetti/Confetti';
import { ProvideAuthContext } from '../../context/AuthContext';
import { ProvideShareToolContext } from '../../context/ShareToolContext';
import { EmailShareTab } from './EmailShareTab';
import { LinkOrPdfShareTab } from './LinkOrPdfShareTab';
import { standaloneTrackInteraction, standaloneTrackPageView } from '../../usage-tracker/standaloneTrack';
import { ProvideEmailShareTabContext } from '../../context/EmailShareTabContext';
import { ProvideLinkOrPdfShareTabContext } from '../../context/LinkOrPdfShareTabContext';
import { CrmEmailShareTab } from './CrmEmailShareTab';
import { getDefaultSelectedTab } from '../../utils/shareTool';
import { CenteredSpinner } from '../CenteredSpinner';
import { TestCheckoutShareTab } from './TestCheckoutShareTab';
import { EmbedCodeShareTab } from './EmbedCodeShareTab';
import { QrCodeShareTab } from './QrCodeShareTab';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { setStandaloneTracker } from '../../usage-tracker/standaloneTrack';
import { Tab, Tabs } from 'foundations-components/transitional/components/Tabs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// TODO: Change the 'tabs' prop to accept an array instead of an object to allow consumers to define the tab order
const tabOrder = [{
  name: 'testCheckoutShareTab',
  Component: TestCheckoutShareTab
}, {
  name: 'emailShareTab',
  Component: EmailShareTab
}, {
  name: 'crmEmailShareTab',
  Component: CrmEmailShareTab
}, {
  name: 'linkOrPdfShareTab',
  Component: LinkOrPdfShareTab
}, {
  name: 'qrCodeShareTab',
  Component: QrCodeShareTab
}, {
  name: 'embedCodeShareTab',
  Component: EmbedCodeShareTab
}];
const tabContextProviders = {
  emailShareTab: ProvideEmailShareTabContext,
  linkOrPdfShareTab: ProvideLinkOrPdfShareTabContext
};
export const ShareToolModal = ({
  headerTitle,
  showCelebration,
  defaultSelectedTab,
  isLoading,
  tabs,
  onClose,
  hasScope,
  trackerProps
}) => {
  const calculatedDefaultSelectedTab = useMemo(() => {
    return getDefaultSelectedTab(tabs, tabOrder.map(({
      name
    }) => name), defaultSelectedTab);
  }, [defaultSelectedTab, tabs]);
  const [selectedTab, setSelectedTab] = useState(calculatedDefaultSelectedTab);
  const footerRef = useRef(null);
  useEffect(() => {
    setSelectedTab(calculatedDefaultSelectedTab);
  }, [calculatedDefaultSelectedTab]);
  const handleTabChange = event => {
    setSelectedTab(String(event.target.value));
    standaloneTrackInteraction({
      action: 'tab changed',
      value: String(event.target.value)
    });
  };
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    standaloneTrackInteraction({
      action: 'close modal'
    });
  }, [onClose]);
  setStandaloneTracker({
    trackerInstance: trackerProps.tracker,
    trackerProperties: Object.assign({
      component: 'ShareToolModal'
    }, trackerProps.trackingProperties)
  });
  useEffect(() => {
    standaloneTrackPageView();
  }, []);
  const wrapWithTabContexts = children => Object.entries(tabs).reduce((acc, [key, tab]) => {
    const TabContextProvider = tabContextProviders[key];
    return TabContextProvider ? /*#__PURE__*/_jsx(TabContextProvider, {
      tab: tab,
      children: acc
    }) : acc;
  }, children);
  const uiTabs = useMemo(() => {
    return tabOrder.map(({
      name,
      Component
    }) => {
      const tabProps = tabs[name];
      if (!tabProps) return null;
      return /*#__PURE__*/_jsx(Tab, {
        title: tabProps.title,
        tabId: name,
        children: /*#__PURE__*/_jsx(Flex, {
          justifySelf: "center",
          blockSize: "60vh",
          paddingBlockStart: "600",
          paddingBlockEnd: "400",
          inlineSize: "100%",
          justifyContent: "center",
          minBlockSize: "60vh",
          children: /*#__PURE__*/_jsx(Component, Object.assign({}, tabProps))
        })
      }, name);
    }).filter(Boolean);
  }, [tabs]);
  return /*#__PURE__*/_jsx(ProvideAuthContext, {
    hasScope: hasScope,
    children: /*#__PURE__*/_jsx(ProvideShareToolContext, {
      footerRef: footerRef,
      closeModal: handleClose,
      children: /*#__PURE__*/_jsxs(Modal, {
        size: "auto",
        width: "80vw",
        "data-test-id": "share-tool-modal",
        onEsc: handleClose,
        children: [showCelebration && /*#__PURE__*/_jsx(Confetti, {
          pieces: 60
        }), /*#__PURE__*/_jsxs(DialogHeader, {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h2",
            children: headerTitle
          }), /*#__PURE__*/_jsx(DialogCloseButton, {
            onClick: handleClose,
            "data-test-id": "share-tool-x-button"
          })]
        }), /*#__PURE__*/_jsx(DialogBody, {
          children: wrapWithTabContexts(isLoading ? /*#__PURE__*/_jsx(CenteredSpinner, {
            "data-test-id": "share-tool-modal-loading"
          }) : /*#__PURE__*/_jsx(Tabs, {
            selected: selectedTab,
            onSelectedChange: handleTabChange,
            panelStyles: {
              padding: '0'
            },
            children: uiTabs
          }))
        }), /*#__PURE__*/_jsx("div", {
          ref: footerRef
        })]
      })
    })
  });
};